import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import countries from 'i18n-iso-countries';
import enCountries from 'i18n-iso-countries/langs/en.json';
import ruCountries from 'i18n-iso-countries/langs/ru.json';
import { useAuth } from '../context/AuthContext';
import { useActiveCompanyPicker } from '../context/ActiveCompanyPickerContext';
import { useTheme } from '../context/ThemeContext';
import { useEnvironment } from '../context/EnvironmentContext';
import { useOperations } from '../context/OperationsContext';

countries.registerLocale(enCountries);
countries.registerLocale(ruCountries);

const NARROW_BREAKPOINT = 600;

function useIsNarrow() {
  const [isNarrow, setIsNarrow] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.innerWidth < NARROW_BREAKPOINT;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(`(max-width: ${NARROW_BREAKPOINT - 1}px)`);
    const update = () => setIsNarrow(mediaQuery.matches);
    update();
    mediaQuery.addEventListener('change', update);
    return () => mediaQuery.removeEventListener('change', update);
  }, []);

  return isNarrow;
}

export default function SideMenu() {
  const { currentTheme, theme } = useTheme();
  const { environment } = useEnvironment();
  const { dashboard, incidents, setIncidentDrawerOpen } = useOperations();
  const isNarrow = useIsNarrow();
  const [open, setOpen] = useState(() => {
    if (typeof window === 'undefined') {
      return true;
    }
    return window.innerWidth >= NARROW_BREAKPOINT;
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { openCompanyPicker } = useActiveCompanyPicker();
  const hasIncident = incidents.length > 0 || (dashboard?.services || []).some((service) => !['ok', 'healthy'].includes(service.status));

  useEffect(() => {
    if (isNarrow) {
      setOpen(false);
    }
  }, [isNarrow]);

  const roles = Array.isArray(user?.roles) ? user.roles : [];
  const isAdmin = roles.includes('administrator');

  const myCompanyId =
    user?.myCompany?.id ||
    user?.myCompany?.company_id ||
    user?.myCompany?.companyId ||
    '';

  const activeCompanyId =
    user?.activeCompany?.id ||
    user?.activeCompany?.company_id ||
    user?.activeCompany?.companyId ||
    '';

  const activeCompanyName =
    user?.activeCompany?.name ||
    '';

  const shouldShowActiveCompany =
    isAdmin &&
    Boolean(activeCompanyId) &&
    String(activeCompanyId) !== String(myCompanyId);

  const shouldPromptActiveCompany =
    isAdmin &&
    !activeCompanyId;

  const activeCompanyCountryNumeric =
    user?.activeCompany?.country_code ?? user?.activeCompany?.countryCode ?? user?.activeCompany?.country ?? null;

  const activeCompanyAlpha2 = useMemo(() => {
    if (activeCompanyCountryNumeric == null) return null;
    const digits = String(activeCompanyCountryNumeric).replace(/[^\d]/g, '');
    if (!digits) return null;
    const n = digits.padStart(3, '0');
    try {
      return countries.numericToAlpha2(n);
    } catch {
      return null;
    }
  }, [activeCompanyCountryNumeric]);

  const activeCompanyCountryTitle = useMemo(() => {
    if (!activeCompanyAlpha2) return null;
    const lng = (i18n?.language || 'en').toLowerCase().startsWith('ru') ? 'ru' : 'en';
    try {
      return countries.getName(activeCompanyAlpha2, lng) || countries.getName(activeCompanyAlpha2, 'en') || activeCompanyAlpha2;
    } catch {
      return activeCompanyAlpha2;
    }
  }, [activeCompanyAlpha2, i18n?.language]);

  const copyActiveCompanyInfo = async () => {
    const name = String(activeCompanyName || '').trim();
    const id = String(activeCompanyId || '').trim();
    const text = [name, id ? `ID: ${id}` : ''].filter(Boolean).join('\n');
    if (!text) {
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      alert(t('copiedToClipboard'));
    } catch {
      // ignore clipboard failures
    }
  };

  const items = useMemo(
    () => {
      const base = [
        { label: 'Main', path: '/', icon: 'oi oi-dashboard' },
        { label: t('calculations'), path: '/calculations', iconImage: '/icons/cargo-truck.png' },
      ];

      if (isAdmin) {
        base.push({ label: t('incomingWebhooks'), path: '/incoming_webhooks', icon: 'oi oi-flash' });
      }

      base.push(
        { label: t('outgoingWebhooks'), path: '/outgoing_webhooks', icon: 'oi oi-bullhorn' },
        { label: t('integrations'), path: '/integrations', icon: 'oi oi-link-intact' },
        { label: t('packaging'), path: '/packaging', icon: 'oi oi-box' },
      );

      if (isAdmin) {
        base.push({ label: t('pricingRules'), path: '/pricing_rules', icon: 'oi oi-spreadsheet' });
        base.push({ label: t('apiKeys'), path: '/api_keys', icon: 'oi oi-key' });
        base.push({ label: t('clients'), path: '/clients', icon: 'oi oi-people' });
        base.push({ label: t('observability'), path: '/observability', icon: 'oi oi-graph' });
        base.push({ label: t('demo'), path: '/demo', icon: 'oi oi-media-play' });
      }

      return base;
    },
    [t, isAdmin],
  );

  const expanded = open && !isNarrow;

  const containerStyle = {
    alignSelf: 'stretch',
    minHeight: '100vh',
    background: currentTheme.background,
    borderRight: `1px solid ${currentTheme.border}`,
    boxShadow: currentTheme.cardShadow,
    transition: 'width 0.2s ease',
    width: expanded ? 244 : 64,
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    overflow: 'hidden',
  };

  const iconButtonStyle = {
    width: 44,
    height: 44,
    borderRadius: 10,
    border: `1px solid ${currentTheme.border}`,
    cursor: 'pointer',
    background: currentTheme.surface,
    color: currentTheme.textSecondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
  };

  return (
    <aside style={containerStyle}>
      <button type="button" className={`sidebar-status-rail ${hasIncident ? 'has-incident' : 'is-healthy'}`} title={hasIncident ? 'Open operational incidents' : 'Open system status'} onClick={() => setIncidentDrawerOpen(true)} aria-label="Open operational status"><span /></button>
      <div style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          style={iconButtonStyle}
          aria-label={expanded ? t('closeMenu') : t('openMenu')}
          aria-expanded={expanded}
          disabled={isNarrow}
        >
          <span className="oi oi-menu" aria-hidden="true" />
        </button>

        {expanded && <div style={{ color: theme === 'light' ? '#000000' : '#ffffff', fontWeight: 800, letterSpacing: '-0.055em', overflow: 'hidden', textOverflow: 'ellipsis' }}>filuetship</div>}
      </div>

      <nav
        style={{
          padding: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
        }}
      >
        {items.map((item) => {
          const active =
            location.pathname === item.path ||
            (location.pathname.startsWith(item.path) && location.pathname.charAt(item.path.length) === '/');
          return (
            <button
              key={item.path}
              type="button"
              onClick={() => navigate(item.path)}
              style={{
                width: '100%',
                border: 'none',
                cursor: 'pointer',
                background: active ? currentTheme.surface : 'transparent',
                color: active ? currentTheme.text : currentTheme.textSecondary,
                border: active ? `1px solid ${currentTheme.border}` : '1px solid transparent',
                borderRadius: 10,
                padding: expanded ? '10px 12px' : '10px 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: expanded ? 'flex-start' : 'center',
                gap: expanded ? 10 : 0,
                transition: 'all 0.2s ease',
              }}
              title={item.label}
            >
              {item.iconImage ? (
                <span
                  aria-hidden="true"
                  style={{
                    width: 16,
                    height: 16,
                    display: 'inline-block',
                    backgroundColor: active ? '#fff' : currentTheme.text,
                    maskImage: `url(${item.iconImage})`,
                    WebkitMaskImage: `url(${item.iconImage})`,
                    maskRepeat: 'no-repeat',
                    WebkitMaskRepeat: 'no-repeat',
                    maskPosition: 'center',
                    WebkitMaskPosition: 'center',
                    maskSize: 'contain',
                    WebkitMaskSize: 'contain',
                    flexShrink: 0,
                  }}
                />
              ) : (
                <span className={item.icon} aria-hidden="true" />
              )}
              {expanded && <span style={{ fontWeight: 500 }}>{item.label}</span>}
            </button>
          );
        })}
      </nav>

      {shouldPromptActiveCompany && expanded && (
        <div style={{ marginTop: 'auto', flexShrink: 0, padding: 12 }}>
          <button
            type="button"
            onClick={openCompanyPicker}
            style={{
              width: '100%',
              borderRadius: 12,
              border: `1px solid ${currentTheme.border}`,
              background: currentTheme.background,
              padding: 10,
              cursor: 'pointer',
              textAlign: 'center',
              color: theme === 'dark' ? '#ffffff' : '#0d2bff',
              font: 'inherit',
              fontWeight: 700,
            }}
          >
            {t('noActiveCompany')}
          </button>
        </div>
      )}

      {shouldShowActiveCompany && expanded && (
        <div style={{ marginTop: 'auto', flexShrink: 0, padding: 12 }}>
          <div
            style={{
              borderRadius: 12,
              border: `1px solid ${currentTheme.border}`,
              background: currentTheme.background,
              padding: 10,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: currentTheme.textSecondary, marginBottom: 4 }}>
              {t('activeCompany')}
            </div>
            <div
              style={{
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                minWidth: 0,
              }}
            >
              <span
                role="button"
                tabIndex={0}
                onClick={copyActiveCompanyInfo}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    copyActiveCompanyInfo();
                  }
                }}
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                  cursor: 'pointer',
                }}
                title={activeCompanyId ? `ID: ${activeCompanyId}` : undefined}
              >
                {activeCompanyName || activeCompanyId}
              </span>
              {activeCompanyAlpha2 && (
                <span
                  className={`fi fi-${String(activeCompanyAlpha2).toLowerCase()}`}
                  aria-hidden="true"
                  title={activeCompanyCountryTitle ? `${activeCompanyCountryTitle} (${activeCompanyAlpha2})` : activeCompanyAlpha2}
                  style={{ flexShrink: 0, fontSize: 12, lineHeight: 1, maxHeight: 12 }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
